import React, { memo } from "react";
import { 
  ComposableMap, 
  Geographies, 
  Geography,
  ZoomableGroup
} from "react-simple-maps";
import { scaleQuantile } from "d3-scale";
import { Box, Typography, Tooltip } from "@mui/material";
import { formatBRL, formatNumber } from "./utils/formatters";

// GeoJSON do Brasil com estados
const geoUrl = "https://raw.githubusercontent.com/codeforamerica/click_that_hood/master/public/data/brazil-states.geojson";

// Função para encontrar dados de um estado
const findStateData = (stateId, data) => {
  if (!data) return null;
  // Verificar diferentes formatos e fazer o match caso insensitivo
  return data.find(item => 
    item.state.toUpperCase() === stateId.toUpperCase() || 
    item.state.toUpperCase().includes(stateId.toUpperCase()) ||
    stateId.toUpperCase().includes(item.state.toUpperCase())
  );
};

// Paleta de cores para o mapa de calor
const COLOR_RANGE = [
  "#e6f2ff",
  "#c3e0ff",
  "#80c1ff", 
  "#4da6ff", 
  "#1a8cff", 
  "#0066cc", 
  "#004d99", 
  "#003366"
];

const BrazilMap = ({ data }) => {
  // Verifica se há dados para usar no mapa
  if (!data || data.length === 0) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: 400,
        border: '1px dashed #ccc',
        borderRadius: 2,
        flexDirection: 'column',
        gap: 2
      }}>
        <Typography variant="body1" color="text.secondary">
          Dados não disponíveis para visualização no mapa
        </Typography>
      </Box>
    );
  }
  // Prepara os dados para formatação e coloração
  const dataWithState = data.map(d => ({
    state: d.state || "",
    value: d.orders || 0, // Usaremos esse campo para o tamanho/cor dos círculos no mapa
    revenue: d.revenue || 0
  }));

  // Escala para coloração baseada na quantidade de pedidos
  const colorScale = scaleQuantile()
    .domain(dataWithState.map(d => d.value))
    .range(COLOR_RANGE);

  return (
    <Box sx={{ position: "relative" }}>
      <Box sx={{
        width: "100%",
        height: 400,
        border: '1px solid #eee',
        borderRadius: 2,
        overflow: 'hidden'
      }}>        <ComposableMap
          projection="geoMercator"
          projectionConfig={{
            scale: 900,
            center: [-53, -15.5]
          }}
          style={{
            width: "100%", 
            height: "100%",
            backgroundColor: "#f7fbff"
          }}
        >
          <ZoomableGroup zoom={1} center={[-55, -15]}>
            <Geographies geography={geoUrl}>
              {({ geographies }) =>
                geographies.map(geo => {                  // Tenta extrair o código do estado do geojson em vários formatos possíveis
                  const stateId = geo.properties.sigla || geo.properties.codigo || geo.properties.iso_3166_2 || geo.properties.name;
                  // Mapeamento específico para siglas de estados
                  const stateMap = {
                    "SP": "SP", "RJ": "RJ", "MG": "MG", "RS": "RS", "BA": "BA", 
                    "SC": "SC", "PR": "PR", "GO": "GO", "ES": "ES", "MT": "MT",
                    "PE": "PE", "DF": "DF", "MS": "MS", "PA": "PA", "CE": "CE",
                    "TO": "TO", "RN": "RN", "MA": "MA", "PB": "PB", "RO": "RO",
                    "AL": "AL", "PI": "PI", "SE": "SE", "AC": "AC", "AM": "AM",
                    "AP": "AP", "RR": "RR"
                  };
                  
                  const mappedStateId = stateMap[stateId] || stateId;
                  const currentState = findStateData(mappedStateId, dataWithState);
                  
                  return (                    <Tooltip
                      key={geo.rsmKey}
                      title={
                        <Box sx={{ p: 1 }}>
                          <Typography variant="body2" fontWeight="bold" sx={{ borderBottom: '1px solid #eee', pb: 0.5, mb: 0.5 }}>
                            {geo.properties.name || stateId}
                          </Typography>
                          <Typography variant="body2" sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                            <span>Pedidos:</span> <strong>{currentState ? formatNumber(currentState.value) : "0"}</strong>
                          </Typography>
                          <Typography variant="body2" sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                            <span>Receita:</span> <strong>{currentState ? formatBRL(currentState.revenue) : "R$ 0,00"}</strong>
                          </Typography>
                        </Box>
                      }
                      arrow
                      placement="top"
                      sx={{
                        '& .MuiTooltip-tooltip': {
                          backgroundColor: 'white',
                          color: 'rgba(0, 0, 0, 0.87)',
                          boxShadow: '0px 2px 6px rgba(0,0,0,0.2)',
                          borderRadius: 1,
                          minWidth: 180
                        }
                      }}
                    >
                      <Geography
                        geography={geo}
                        fill={currentState ? colorScale(currentState.value) : "#EEE"}
                        stroke="#FFF"
                        strokeWidth={0.5}                        style={{
                          default: {
                            outline: "none",
                            transition: "all 250ms"
                          },
                          hover: {
                            outline: "none",
                            fill: "#1976d2",
                            cursor: "pointer",
                            transform: "translate(-2px, -2px)",
                            filter: "drop-shadow(2px 2px 2px rgba(0,0,0,0.3))",
                            zIndex: 1000
                          },
                          pressed: {
                            outline: "none",
                            fill: "#0d47a1"
                          }
                        }}
                      />
                    </Tooltip>
                  );
                })
              }
            </Geographies>
          </ZoomableGroup>
        </ComposableMap>
      </Box>

      {/* Legenda do mapa */}
      <Box sx={{ 
        display: "flex", 
        justifyContent: "center", 
        mt: 1, 
        flexWrap: 'wrap',
        gap: 1
      }}>
        <Typography variant="caption" color="text.secondary">Intensidade de vendas: </Typography>
        {COLOR_RANGE.map((color, index) => (
          <Box 
            key={index}
            sx={{
              backgroundColor: color,
              width: 20,
              height: 10,
              border: '1px solid #DDD'
            }}
          />
        ))}
        <Typography variant="caption" color="text.secondary">
          (Menor — Maior)
        </Typography>
      </Box>
    </Box>
  );
};

export default memo(BrazilMap);
